import { useParams } from 'react-router-dom'
import { ReactiveJsonRoot } from '@ea-lab/reactive-json'
import { useState, useEffect } from 'react'

// Component for routes with dynamic data loading
export function DynamicPage({ rjbuild, dataUrl, plugins }) {
  const params = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const loadData = async () => {
      // Replace URL parameters (e.g., :id) with actual values
      let resolvedDataUrl = dataUrl
      Object.keys(params).forEach(key => {
        resolvedDataUrl = resolvedDataUrl.replace(`:${key}`, params[key])
      })

      try {
        setLoading(true)
        setError(null)

        const response = await fetch(resolvedDataUrl)
        
        // Check if the response is JSON first (Vite dev server returns 200 + HTML for missing files)
        const contentType = response.headers.get('content-type')
        if (!contentType || !contentType.includes('application/json')) {
          // If it's HTML, it's likely a 404 page from the dev server
          if (contentType && contentType.includes('text/html')) {
            throw new Error(`Profile not found - The requested profile does not exist`)
          }
          throw new Error(`Expected JSON data but received ${contentType || 'unknown content type'}`)
        }
        
        // Check if the response status is successful
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error(`Data not found: ${resolvedDataUrl}`)
          }
          throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`)
        }
        
        // Parse JSON with error handling
        let fetchedData
        try {
          const text = await response.text()
          fetchedData = JSON.parse(text)
        } catch (parseError) {
          throw new Error(`Invalid JSON data: ${parseError.message}`)
        }
        
        // Validate that we have data
        if (!fetchedData || typeof fetchedData !== 'object') {
          throw new Error('No valid data received')
        }
        
        setData(fetchedData)
      } catch (err) {
        const errorMessage = `${err.message} (URL: ${resolvedDataUrl})`
        setError(errorMessage)
        console.error('Error loading data:', {
          error: err,
          url: resolvedDataUrl,
          params: params
        })
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [dataUrl, params])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-100">
        <div className="bg-white rounded-lg shadow p-8 max-w-lg mx-4">
          <div className="text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Data Loading Error</h2>
            <p className="text-red-600 mb-6">{error}</p>
            <div className="space-y-2 text-slate-600">
              <p>Possible solutions:</p>
              <ul className="text-left list-disc list-inside space-y-1">
                <li>Try an existing profile: /profile/1, /profile/2, or /profile/3</li>
                <li>Check if the profile ID is correct</li>
                <li>Verify the data file exists in the correct format</li>
              </ul>
            </div>
            <button 
              onClick={() => window.location.href = '/'}
              className="btn btn-primary mt-6"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <ReactiveJsonRoot
      rjBuildUrl={rjbuild}
      rjBuildFetchMethod="GET"
      dataOverride={data}
      plugins={plugins}
    />
  )
}
